import * as React from 'react';
import { RowContainerComponentProps } from '../common/types';
import { FileUploaderItem } from './file-uploader-item';
import { XMLHttpRequestReadyState } from './enums';
import { FileUploaderAddButton } from 'ui/file-uploader/file-uploader-attach-button';
import MA from 'moving-average';
import { withDragDrop, DragDropProps } from 'ui/file-uploader/with-dragdrop';
import { FiChevronDown } from 'react-icons/fi';
import styles from './file-uploader.scss';
import { toastFactory } from 'app/infrastructure/notifications/toast-factory';
import {
  makeProgressFile,
  makeItemPropsFromProgressFile,
  addItemPropsFromUploadedFile,
  makeItemPropsFromUploadedFile,
  ProgressFile
} from 'ui/file-uploader/file';
import { AttachmentViewer } from 'ui/attachment-viewer/attachment-viewer';
import { MalwareScanStatusValue } from 'ui/file-uploader/malware-scan-status';

interface FileUploaderState {
  progress?: ProgressFile[];
  errors: string[];
  showMoreItems: boolean;
  viewAttachment?: number;
}

interface UploadedFile {
  id?: number; // No. column of attachment row
  blobId?: number; // Blob id of attachment row if the file has been uploaded
  name: string;
  title: string;
  type: string;
  size: number;
  thumbnailSize?: number;
  attachedAt?: Date;
  malwareScanStatus?: MalwareScanStatusValue;
}

/**
 * FileUploader component let you upload multiple files when clicked on browse button
 */
export interface FileUploaderComponentProps {
  /**
   * Information text about what is the maximum file size
   */
  textFileUploadInfoLine1?: string;
  /**
   * Information text about what is the maximum file size
   */
  textFileUploadInfoLine2?: string;
  /**
   * Browse button text
   */
  textAddButton?: string;
  /**
   * Error message to show if the user tries to upload a file that is too large
   */
  textFileTooLargeError?: string;
  /**
   * Error message to show if any other error occurs while uploading the file
   */
  textFileUploadError?: string;
  /**
   * Used when displaying time left (eg. 1h)
   */
  textHourPostfix?: string;
  /**
   * Used when displaying time left (eg. 1m)
   */
  textMinutePostfix?: string;
  /**
   * Used when displaying time left (eg. 1s)
   */
  textSecondPostfix?: string;
  /**
   * Used when a value can not be calculated (eg. n/a)
   */
  textNotAvailable?: string;
  /**
   * Label for button to confirm the edited title
   */
  textDone?: string;
  /**
   * Dropdown text for edit title
   */
  textEditTitle?: string;
  /**
   * Dropdown text for delete attachment
   */
  textDelete?: string;
  /**
   * Dialog message for confirmation of deleting attachment
   */
  textDeleteMessage?: string;
  /**
   * Dropdown text for download file
   */
  textDownloadFile?: string;
  textUseAsMainImage?: string;
  /**
   * Continue button text
   */
  textContinue?: string;
  /**
   * Cancel button text
   */
  textCancel?: string;
  /**
   * The text to show when items are collapsed behind a show more link
   */
  textShowMore?: string;
  /**
   * The text to show when items are expanded
   */
  textHideMore?: string;
  /**
   * Max amount of items to show before collapsing behind a "show more" link
   */
  maxItemsToShow: number;
  /**
   * Maximum files the client will upload in parallell. Default 6.
   */
  uploadsInParallel: number;
  /**
   * Maximum file size the client can upload in MB. Default 100.
   */
  maxSizeMB: number;
  /**
   * How long error messsages should appear before they are cleared from the UI (in ms)
   *
   * @internal
   */
  errorMessageTimeout?: number;
  /**
   * Optional. Default is `false`.
   * If the component should display in a read-only state. This means not allowed to add / remove attachments.
   */
  readOnly?: boolean;
  /**
   * Text displayed when no files have been attached
   */
  textNoFilesAttached?: string;
  print?: boolean;
  hideTextNoData?: boolean;
  /**
   * Header Text for file listing panel
   */
   textHeaderFileListing?: string;
  /**
   * @internal
   */
  createUploader?: ()=> Promise<XMLHttpRequest>;
  /**
   * @internal
   */
  addItem?: (blobId: number, name: string) => void;
  /**
   * @internal
   */
  deleteItem?: (id: number) => void;
  /**
   * @internal
   */
  updateItemTitle?: (id: number, name: string) => void;
  setItemAsMain?: (id:number) => void;
  /**
   * @internal
   */
  uploadedFiles?: UploadedFile[];
  /**
   * @internal
   */
  createDownloader?: (blobId: number, name: string) => Promise<void>;
  /**
   * @internal
   */
  loadBlob?: (blobId: number, thumbnail?: boolean) => Promise<Blob>;
}

export type FileUploaderProps = FileUploaderComponentProps &
  DragDropProps &
  RowContainerComponentProps;

export class FileUploaderBase extends React.Component<FileUploaderProps, FileUploaderState> {

  public static defaultProps: Partial<FileUploaderProps> = {
    maxItemsToShow: 10,
    uploadsInParallel: 6,
    maxSizeMB: 100,
    errorMessageTimeout: 6000
  };

  public state: Readonly<FileUploaderState> = {
    progress: [],
    errors: [],
    showMoreItems: false,
    viewAttachment: undefined
  };

  private maxItemsToShowEditMode: number = this.props.maxItemsToShow - 1;
  
  public componentDidUpdate(prevProps: FileUploaderProps) {
    if (this.props.uploadedFiles &&
      this.props.uploadedFiles !== prevProps.uploadedFiles &&
      this.state.progress &&
      this.state.progress.length) {
      this.setState((prevState: FileUploaderState): FileUploaderState => {
        const newItemList = prevState.progress.filter(p => !this.props.uploadedFiles.some(u => p.blobId === u.blobId && !!u.attachedAt));
        if (newItemList.length !== prevState.progress.length) {
          return {
            progress: newItemList
          } as FileUploaderState;
        }

        return undefined as FileUploaderState;
      }, this.fillQueue);
    }
  }

  private addError = (progressFile: ProgressFile, error: string) => {
    const errorMsg = `${progressFile.file.name}: ${error}`;

    if (!this.state.errors.find(e => e === errorMsg)) {
      // In some cases we end up here both from onreadystatechange and onerror
      this.setState({
        errors: [errorMsg, ...this.state.errors]
      } as FileUploaderState);
    }

    setTimeout(() => {
      this.setState({
        errors: this.state.errors.filter(e => e !== errorMsg)
      } as FileUploaderState);
    }, this.props.errorMessageTimeout);

    this.removeProgressInfo(progressFile);
  };

  private removeProgressInfo = (progressFile: ProgressFile) => {
    this.setState((prevState: FileUploaderState): FileUploaderState => (
      {
        progress: prevState.progress.filter(p => p.file !== progressFile.file)
      } as FileUploaderState),
      this.fillQueue);
  };

  private updateFileState = (progressFile: ProgressFile, data: object, callback?: () => any) => {
    this.setState((prevState: FileUploaderState): FileUploaderState => {
      const current = prevState.progress.find(p => p.file === progressFile.file);
      const currentIndex = prevState.progress.indexOf(current);
      const progress = [
        ...prevState.progress.slice(0, currentIndex),
        {
          ...current,
          ...data
        },
        ...prevState.progress.slice(currentIndex + 1)
      ];
      return {
        progress
      } as FileUploaderState;
    }, callback);
  };

  //TODO: move this side effect to actionCreator. This allows a redux dispatch one it completes and removes the memory leak
  // caused by component unmount while upload is running
  private xhrSend = async (progressFile: ProgressFile) => {
    const file = progressFile.file;

    const ma = MA(1000); // Moving average over 1 second
    const xhr = await this.props.createUploader();
    this.updateFileState(progressFile, { isWaiting: false, xhr });
    const formData = new FormData();
    formData.append('file', file, file.name);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const fileProgress = this.state.progress.find(p => p.file === file);
        if (fileProgress) {
          ma.push(Date.now(), e.loaded - fileProgress.uploaded); // Update moving average
          this.updateFileState(progressFile, { uploaded: e.loaded, uploadRate: ma.movingAverage() });
        }
      }
    };

    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequestReadyState.DONE) {
        // NB: it seems like readyState DONE might fire more than once for one file.
        const fileProgress = this.state.progress.find(p => p.file === file);

        if (xhr.status >= 400 || xhr.status === 0) {
          // When a request is aborted, its readyState is changed to 4 (XMLHttpRequest.DONE)
          // and the status code (XMLHttpRequest.status) is set to 0.

          //eslint:disable no-console
          console.log('error', xhr.status, xhr.statusText);
          
          // Show toast message with userMessage if available
          if (xhr.response) {
            try {
              const json = JSON.parse(xhr.response);
              const userMessage = json?.messages?.userMessages?.[0]?.message;
              if (userMessage) {
                const toasts = toastFactory();
                toasts.dispatchError(userMessage);
              }
              const documentError = json?.data?.documents?.[0]?.error;
              this.addError(progressFile, documentError ?? this.props.textFileUploadError);
              return;
            } catch (e) {
              // If parsing fails, fall through to default error handling
            }
          }
          
          this.addError(progressFile, this.props.textFileUploadError);
        } else if (xhr.response && fileProgress) {
          const resp = JSON.parse(xhr.response);
          const blobId = resp.data.documents[0].blobId;
          if (blobId === 0) {
            this.addError(progressFile, resp.data.documents[0].error);
          }
          else {
            this.props.addItem(blobId, fileProgress.title);
            this.updateFileState(progressFile, { progressValue: 100, blobId: blobId });
          }
        }
      }
    };

    xhr.upload.onerror = xhr.onerror = (e) => {
      this.addError(progressFile, this.props.textFileUploadError);
      //eslint:disable no-console
      console.log('onerror', e);
    };

    xhr.send(formData);
  };

  private fillQueue = () => {
    const uploadQueue = this.state.progress.filter(p => p.isWaiting);
    const uploadsInProgress = this.state.progress.filter(p => !p.isWaiting);

    if (uploadsInProgress.length < this.props.uploadsInParallel && uploadQueue.length) {
      uploadQueue
        .slice(0, this.props.uploadsInParallel - uploadsInProgress.length)
        .forEach(p => this.xhrSend(p));
    }
  };

  private handleUpload = (fileList: FileList) => {
    if (!this.props.createUploader || this.props.readOnly) {
      return;
    }

    const newFiles = Array.from(fileList || []);
    const grouped = newFiles.map(makeProgressFile).reduce((aggr, item) => {
      const size = Math.floor(item.file.size / 1024 / 1024);
      if (size <= this.props.maxSizeMB) {
        aggr.ok.push(item);
      }
      else {
        aggr.tooLarge.push(item);
      }
      return aggr;
    }, { ok: [], tooLarge: [] });

    for (const tooLarge of grouped.tooLarge) {
      this.addError(tooLarge, this.props.textFileTooLargeError);
    }

    const newProgressItems: ProgressFile[] = grouped.ok;

    this.setState((prevState: FileUploaderState): FileUploaderState =>
      ({ progress: [...newProgressItems, ...prevState.progress] }) as FileUploaderState,
      this.fillQueue);
  };

  private makePropsForUploadsInProgress = (progressItem: ProgressFile) => {
    let props = makeItemPropsFromProgressFile(progressItem);

    const uploadedItem = this.props.uploadedFiles.find(u => u.blobId === progressItem.blobId);
    if (uploadedItem) {
      props = addItemPropsFromUploadedFile(props, uploadedItem);
    }
    return props;
  };

  private getAllMessages(): string[] {
    const { validation } = this.props;
    const { errors } = this.state;

    return [
      ...(errors ? errors : []),
      ...(validation && validation.messages.length ? validation.messages.map(m => m.message) : [])
    ];
  }

  private renderErrors = () => {
    const allMessages = this.getAllMessages();

    if (allMessages.length === 0) {
      return null;
    }

    return (
      <div className={`alert-danger ${styles.flexValidation}`}>
        <ul>
          {allMessages.map(i => <li className="list-group-item-danger">{i}</li>)}
        </ul>
      </div>
    );
  };

  private handleToggleItemExpand = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      showMoreItems: !this.state.showMoreItems
    } as FileUploaderState);
  };

  private prepareItems = () => {
    // items will be a merge of rows from props, and progress items from state
    return this.state.progress.map(this.makePropsForUploadsInProgress)
      .concat(
        this.props.uploadedFiles ? this.props.uploadedFiles
          .filter(r => !this.state.progress.some(p => p.blobId === r.blobId))
          .map(makeItemPropsFromUploadedFile) : []
      );
  };
  private selectImage = (index:number) => {
    this.setState({viewAttachment: index});
  };

  private handleClosingViewer = () => {
    this.setState({viewAttachment: undefined});
  };

  private renderItems = (itemProps: any) => {

    let itemsToRender = itemProps;
    if (!this.props.print && !this.state.showMoreItems) {
      const itemsToShow = !this.props.readOnly && !!this.props.createUploader ? this.maxItemsToShowEditMode : this.props.maxItemsToShow;
      itemsToRender = itemProps.slice(0, Math.min(itemProps.length, itemsToShow));
    }

    return itemsToRender.map(p => <FileUploaderItem
      key={p.key}
      createDownloader={this.props.createDownloader}
      loadBlob={this.props.loadBlob}
      textHourPostfix={this.props.textHourPostfix}
      textMinutePostfix={this.props.textMinutePostfix}
      textSecondPostfix={this.props.textSecondPostfix}
      textNotAvailable={this.props.textNotAvailable}
      textDone={this.props.textDone}
      textEditTitle={this.props.textEditTitle}
      textDelete={this.props.textDelete}
      textDeleteMessage={this.props.textDeleteMessage}
      textDownloadFile={this.props.textDownloadFile}
      textContinue={this.props.textContinue}
      textUseAsMainImage={this.props.textUseAsMainImage}
      textCancel={this.props.textCancel}
      deleteItem={!this.props.readOnly && this.props.deleteItem}
      updateItemTitle={!this.props.readOnly && this.props.updateItemTitle}
      setItemAsMain={!this.props.readOnly && this.props.setItemAsMain}
      formatDate={this.props.formatDate}
      formatNumber={this.props.formatNumber}
      formatTime={this.props.formatTime}
      onClick={this.selectImage}
      print={this.props.print}
      {...p} />);
  };

  private displayToggleLinkButton = (items: any) => !this.props.readOnly && !!this.props.createUploader ? 
    items.length > this.maxItemsToShowEditMode : items.length > this.props.maxItemsToShow;

  private renderExpander = (itemProps: any) => {
    return !this.props.print && this.displayToggleLinkButton(itemProps)
      ? <button type="button"
          className={`standalone-link ${styles.toggleLinkButton} ${this.state.showMoreItems ? styles.showLess : ''}`}
          onClick={this.handleToggleItemExpand}>
        {this.state.showMoreItems ? this.props.textHideMore : this.props.textShowMore}
        <FiChevronDown />
      </button>
      : null;
  };

  private renderAddButton = () => {
    if (!this.props.createUploader || this.props.readOnly) {
      return null;
    }

    return (
      <div className="row">
        <FileUploaderAddButton
          addButtonText={this.props.textAddButton}
          handleUpload={this.handleUpload}
          required={this.props.label.required} />
      </div>
    );
  };

  public render() {
    const { validation } = this.props;

    const itemProps = this.prepareItems();
    const dragClass = !this.props.readOnly && !!this.props.createUploader && this.props.dragActive ? styles.dragActive : '';
    const dragInfo = [
      <p key={0}>{this.props.textFileUploadInfoLine1}</p>,
      <p key={1}>{this.props.textFileUploadInfoLine2}</p>
    ];

    const hasNoContent = this.props.uploadedFiles && !this.props.uploadedFiles.length && 
      (!this.props.createUploader || this.props.readOnly);

    const noContentInlineStyle = hasNoContent ? styles.noContent : '';

    return (
      <>
      <AttachmentViewer uploadedFiles={this.props.uploadedFiles} loadBlob={this.props.loadBlob}
        onClose={this.handleClosingViewer} initialSelected={this.state.viewAttachment}/>
      <div id={this.props.componentId} className={`${styles.fileUploader} ${noContentInlineStyle} 
        ${validation && validation.messages.length ? 'has-errors' : ''} 
        ${hasNoContent && this.props.hideTextNoData ? styles.compactNoContent : ''}`}>
        {!hasNoContent && !this.props.hideTextNoData && <h3 className={styles.fileHeading}>
          {this.props.textHeaderFileListing || this.props.label.text}
        </h3>}

        <div className={this.getAllMessages().length ? 'has-errors' : ''}>
          {this.renderErrors()}
          <div className={styles.listWrapper}>
            {
              !!this.renderAddButton() &&
              <div className={styles.itemWrapper}>
                <div className={`${styles.dragBox} ${dragClass}`}>
                  <div className={styles.dragDropIcon} />
                  <div className={styles.dragDropText}>
                    {dragInfo}

                  </div>
                </div>
              </div>
            }
            {this.renderItems(itemProps)}
            {hasNoContent && !this.props.hideTextNoData && 
              <p className="no-content">
                {this.props.textNoFilesAttached}
              </p>
            }
          </div>
          {this.renderExpander(itemProps)}
        </div>
        {this.renderAddButton()}
      </div>
      </>
    );
  }
}

export const FileUploader = withDragDrop(FileUploaderBase);
